import _ from 'lodash';
import { useEffect } from 'react';
import Router from 'next/router';

import useUI from 'hooks/use-ui';
import useProductQueryParams from 'hooks/use-product-query-params';
import useDispensaryMenuSections from 'src/dispensary/hooks/use-dispensary-menu-sections';

import { getPersistedValue, setPersistedValue } from 'shared/utils/persisted-values';
import { isBrowser } from 'utils/misc-utils';
import ROUTES from 'src/routes';

const { DISPENSARY_CNAME } = ROUTES;

function determineScrollPos(viewport) {
  if (!viewport) {
    return {
      x: 0,
      y: 0,
    };
  }
  const { offsetTop, parentOffset } = viewport;
  const clientScrollY = isBrowser() ? window.scrollY : 0;
  return {
    x: 0,
    y: offsetTop + parentOffset || parentOffset || clientScrollY,
  };
}

function saveScrollPos(url, viewport) {
  const { x, y } = determineScrollPos(viewport);
  const scrollPos = { x, y };
  setPersistedValue(url, scrollPos);
}

function restoreScrollPos(url, UI) {
  const scrollPos = getPersistedValue(url);
  if (scrollPos) {
    UI?.embeddedEventDispatcher(`iframe:goBack`, { y: scrollPos.y });
    window.scrollTo(scrollPos.x, scrollPos.y);
    resetScrollPos(url);
  }
}

function resetScrollPos(url) {
  setPersistedValue(url, null);
}

export function useScrollRestoration({ router, viewport, skip }) {
  const UI = useUI();
  const { queryParams } = useProductQueryParams();
  const { menuSections } = useDispensaryMenuSections();

  // NOTE: previously called `determineScrollPos(viewport)` here directly in the render body (return
  // value discarded, so it was a no-op besides the side effect of referencing `window`). Now that
  // /stores pages render this layout server-side (ECOMM-8974), that unguarded `window` reference
  // threw `ReferenceError: window is not defined` on every SSR render. determineScrollPos is only
  // needed client-side (via saveScrollPos in the effect below), so the dead call is removed rather
  // than guarded.
  useEffect(() => {
    if (skip) {
      return;
    }

    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';

      const onBeforeUnload = (event) => {
        saveScrollPos(router?.asPath, viewport);
        delete event.returnValue;
      };

      const onRouteChangeStart = () => {
        UI.setSubheaderIsSticking(!UI.isDispensaryHeaderInView);
        UI.previousSearchTerm = queryParams.search;
        saveScrollPos(router?.asPath, viewport);
      };

      const onRouteChangeComplete = (url) => {
        if (UI.shouldRestorePreviousScroll) {
          UI.setShouldRestorePreviousScroll(false);
          restoreScrollPos(url, UI);
          return;
        }

        const onDispensaryPage = _.includes(router?.pathname, 'dispensary');
        const shouldScrollToSubheader = UI.isDutchieMain && UI.subheaderIsSticking && onDispensaryPage;
        if (shouldScrollToSubheader) {
          const onDispoHomepageWithoutMenuSections = router?.pathname === DISPENSARY_CNAME && _.isEmpty(menuSections);

          const headerOffset = UI.globalHeaderHeight + UI.dispensaryHeaderHeight;
          window.scrollTo(0, onDispoHomepageWithoutMenuSections ? 0 : headerOffset);
        } else {
          window.scrollTo(0, 0);
        }
      };

      window.addEventListener('beforeunload', onBeforeUnload);

      if (Router && Router.beforePopState) {
        Router.beforePopState(() => {
          UI.setShouldRestorePreviousScroll(true);
          return true;
        });
      }

      if (Router && Router.events) {
        Router.events.on('routeChangeStart', onRouteChangeStart);
        Router.events.on('routeChangeComplete', onRouteChangeComplete);
      }

      // eslint-disable-next-line consistent-return
      return () => {
        if (Router && Router.events) {
          Router.events.off('routeChangeStart', onRouteChangeStart);
          Router.events.off('routeChangeComplete', onRouteChangeComplete);
        }
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router, viewport, UI, skip, queryParams.search]);
}
